import { useQuery } from '@tanstack/react-query'
import { Coins } from 'lucide-react'
import { useState } from 'react'

import { costSummaryQuery, ledgerQuery, type CostGroupBy } from '@/api/costs'
import { Chip } from '@/components/ui/chip'
import { cn } from '@/components/ui/utils'
import { formatClock, formatMs, formatUsd } from '@/lib/format'

/**
 * What the studio has spent, grouped the way you ask (§5.9).
 *
 * Every figure here is the CLI's own list-price **estimate**, never a bill —
 * under a subscription there is no per-call charge at all. The card says
 * that once, at the top, instead of decorating each number with a caveat.
 * Rows with `cost_basis='unknown'` (a killed or timed-out call) are already
 * excluded by the server's averages.
 */

const GROUPS: { value: CostGroupBy; label: string }[] = [
  { value: 'day', label: 'By day' },
  { value: 'kind', label: 'By kind of call' },
  { value: 'model', label: 'By model' },
  { value: 'purpose', label: 'By purpose' },
]

const BILLING_NOTE: Record<string, string> = {
  api: 'List-price estimates from the CLI, not an invoice.',
  subscription: 'A Claude subscription pays for these calls; the figures are estimates for comparison only.',
  mock: 'Mock mode — nothing was sent and nothing was charged.',
}

export function CostSummary() {
  const [groupBy, setGroupBy] = useState<CostGroupBy>('day')
  const summary = useQuery(costSummaryQuery({ group_by: groupBy }))
  const ledger = useQuery(ledgerQuery({ limit: 20 }))

  const s = summary.data
  const rows = s?.rows ?? []
  const max = rows.reduce((m, r) => Math.max(m, r.usd), 0)

  return (
    <section className="card p-5" aria-labelledby="costs-heading">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="costs-heading" className="flex items-center gap-2 text-lg font-semibold text-ink">
          <Coins className="size-5 text-ink-2" aria-hidden />
          Spending
        </h2>
        {s ? (
          <p className="text-sm text-ink-2">
            Today <span className="font-semibold text-ink tabular-nums">{formatUsd(s.today_usd)}</span>
            {s.daily_budget_usd > 0 ? <span className="text-ink-3"> of {formatUsd(s.daily_budget_usd)}</span> : null}
          </p>
        ) : null}
      </header>

      {s ? <p className="mt-1 text-xs text-ink-3">{BILLING_NOTE[s.billing] ?? 'Estimates, not a bill.'}</p> : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {GROUPS.map((g) => (
          <Chip key={g.value} size="sm" selected={groupBy === g.value} onClick={() => setGroupBy(g.value)}>
            {g.label}
          </Chip>
        ))}
      </div>

      {summary.isPending ? (
        <p className="mt-4 text-sm text-ink-2">Adding it up…</p>
      ) : summary.isError ? (
        <p className="mt-4 text-sm text-ink-2">The ledger could not be read right now.</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-ink-2">Nothing has been spent yet.</p>
      ) : (
        <table className="mt-4 w-full text-sm">
          <caption className="sr-only">Spending {GROUPS.find((g) => g.value === groupBy)?.label.toLowerCase()}</caption>
          <thead>
            <tr className="text-left text-xs text-ink-3">
              <th scope="col" className="py-1 font-medium">
                {groupBy === 'day' ? 'Day' : groupBy === 'model' ? 'Model' : groupBy === 'kind' ? 'Kind' : 'Purpose'}
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Calls
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Failed
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Average
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Estimate
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-line">
                <th scope="row" className="relative py-1.5 pr-2 text-left font-medium text-ink">
                  <span className="relative z-10">{r.key}</span>
                  <span
                    className="absolute inset-y-0.5 left-0 -z-0 rounded bg-accent-soft"
                    style={{ width: max > 0 ? `${Math.round((r.usd / max) * 100)}%` : 0 }}
                    aria-hidden
                  />
                </th>
                <td className="py-1.5 text-right tabular-nums text-ink-2">{r.n}</td>
                <td className={cn('py-1.5 text-right tabular-nums', r.errors > 0 ? 'text-danger' : 'text-ink-3')}>{r.errors}</td>
                <td className="py-1.5 text-right tabular-nums text-ink-3">{formatMs(r.avg_ms)}</td>
                <td className="py-1.5 text-right tabular-nums font-medium text-ink">{formatUsd(r.usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {ledger.data && ledger.data.items.length > 0 ? (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-ink-2 hover:text-ink">Last {ledger.data.items.length} calls</summary>
          <ul className="mt-2 flex flex-col gap-1 text-xs">
            {ledger.data.items.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-2 border-b border-line pb-1 last:border-b-0">
                <span className="text-ink-3">{formatClock(row.ts)}</span>
                <span className="font-medium text-ink">{row.purpose}</span>
                <span className="text-ink-2">{row.model ?? '—'}</span>
                {row.is_error ? <span className="text-danger">{row.error_kind ?? 'failed'}</span> : null}
                <span className="ml-auto tabular-nums text-ink-2">{formatUsd(row.cost_usd)}</span>
                {row.cost_basis === 'unknown' ? <span className="text-ink-3">(no envelope)</span> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  )
}
